// Product.js
import DOMPurify from "dompurify";
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { Link, useLocation } from "react-router-dom";
import api from "../api/api";
import ProductCard from "./ProductCard";
import { getImageUrl } from "../utils/imageUrl";
import { FiArrowRight, FiClock, FiLayers } from "react-icons/fi";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const fetchProductsFromDatabase = async (lang, pageKey, tag) => {
  const params = new URLSearchParams();
  if (lang) params.append("lang", lang);
  if (pageKey) params.append("page_key", pageKey);
  if (tag) params.append("tag", tag);

  const response = await fetch(
    `${BACKEND_URL}/api/products?${params.toString()}`,
  );
  if (!response.ok) {
    throw new Error("Failed to fetch products");
  }
  return response.json();
};

const getPlacementSortMeta = (product, sectionKey) => {
  if (!sectionKey) {
    return { featured: 0, order: Number.MAX_SAFE_INTEGER };
  }

  const placements = Array.isArray(product?.pagePlacements)
    ? product.pagePlacements
    : [];
  const placement = placements.find(
    (entry) => String(entry?.page_key || "").toLowerCase() === sectionKey,
  );

  return {
    featured: placement?.is_featured ? 1 : 0,
    order: Number(placement?.sort_order ?? Number.MAX_SAFE_INTEGER),
  };
};

const getThemeByCollection = (title, pageKey, tag) => {
  const normalized = String(pageKey || tag || title || "").toLowerCase();

  if (normalized.includes("wheat")) {
    return {
      hero: "from-[#fff4e6] via-[#fffaf4] to-[#f7dfbf]",
      accent: "#b76c36",
      chip: "bg-[#fff0df] text-[#9a582b] border-[#f1d2ae]",
    };
  }

  if (normalized.includes("japan")) {
    return {
      hero: "from-[#fff4f4] via-[#fffafa] to-[#f3d9d9]",
      accent: "#a92827",
      chip: "bg-[#fff1f1] text-[#922f2f] border-[#efc8c8]",
    };
  }

  if (normalized.includes("kitchen")) {
    return {
      hero: "from-[#f5f9ed] via-[#fbfdf7] to-[#e5ecc9]",
      accent: "#6d7a28",
      chip: "bg-[#eef5dc] text-[#637123] border-[#d7e3ae]",
    };
  }

  if (normalized.includes("handmade")) {
    return {
      hero: "from-[#f6eefc] via-[#fcfbff] to-[#e7daf4]",
      accent: "#6d4a8d",
      chip: "bg-[#f1e8fb] text-[#68468a] border-[#ddcff1]",
    };
  }

  if (normalized.includes("wall") || normalized.includes("decor")) {
    return {
      hero: "from-[#fff1f1] via-[#fff9f9] to-[#f2d3d3]",
      accent: "#a61d1d",
      chip: "bg-[#ffe7e7] text-[#8f2323] border-[#efc6c6]",
    };
  }

  return {
    hero: "from-[#fff3f8] via-[#fffdfd] to-[#f3e3ea]",
    accent: "#83375B",
    chip: "bg-[#f8eaf0] text-[#83375B] border-[#ead2dd]",
  };
};

const isWheatCollection = (title, pageKey, tag) => {
  const normalized = String(pageKey || tag || title || "").toLowerCase();
  return normalized.includes("wheat");
};

const EmptyCollectionState = ({
  title,
  subtitle,
  theme,
  isAllProductsPage,
}) => {
  const { t } = useTranslation();
  const safeTitle = title || t("navbar.products", "Products");

  return (
    <div className="mt-10 overflow-hidden rounded-[2rem] border border-white/70 bg-white/70 shadow-[0_35px_90px_-55px_rgba(131,55,91,0.45)] backdrop-blur">
      <div
        className={`relative overflow-hidden bg-gradient-to-br ${theme.hero} px-6 py-10 sm:px-8 lg:px-10`}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.58),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.22),transparent_28%)]" />
        <div className="relative grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
          <div className="text-center lg:text-left">
            <div
              className="inline-flex items-center gap-2 rounded-full border border-white/75 bg-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] shadow-sm backdrop-blur"
              style={{ color: theme.accent }}
            >
              <FiClock />
              {t("comingSoon.badge", "Coming Soon")}
            </div>
            <h2 className="mt-5 max-w-3xl text-2xl font-semibold tracking-normal text-[#4a3640] sm:text-4xl lg:text-5xl">
              {t("comingSoon.title", {
                collection: safeTitle,
                defaultValue: "{{collection}} is almost here.",
              })}
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-[#6f5b64] sm:text-base">
              {t("comingSoon.description", {
                collection: safeTitle,
                defaultValue:
                  "{{collection}} is currently being prepared with the same quality, styling, and product standards used across our main collections.",
              })}
            </p>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-[#7d6470]">
              {t(
                "comingSoon.subtext",
                "You can browse available collections now, and return shortly for the full release on this page.",
              )}
            </p>

            <div className="mt-7 flex flex-wrap gap-3 justify-center lg:justify-start">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold tracking-[0.12em] text-white transition-all duration-300 hover:-translate-y-0.5"
                style={{ backgroundColor: theme.accent }}
              >
                {t("comingSoon.primaryCta", "Browse Available Products")}
                <FiArrowRight />
              </Link>
              <Link
                to="/contact-us"
                className="inline-flex items-center gap-2 rounded-full border border-white/80 bg-white/72 px-5 py-3 text-sm font-semibold tracking-[0.12em] text-[#5b3d49] transition-all duration-300 hover:bg-white"
              >
                {t("comingSoon.secondaryCta", "Contact Us")}
              </Link>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
            <div className="rounded-[1.6rem] border border-white/75 bg-white/70 p-5 shadow-sm backdrop-blur text-center sm:text-left lg:text-left">
              <div
                className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/90 text-xl shadow-sm mx-auto sm:mx-0 lg:mx-0"
                style={{ color: theme.accent }}
              >
                <FiLayers />
              </div>
              <h3 className="mt-4 text-lg font-bold text-[#2d1f26]">
                {t("comingSoon.cardTitle", "In the oven")}
              </h3>
              <p className="mt-2 text-sm leading-6 text-[#6f5b64]">
                {t(
                  "comingSoon.cardBody",
                  "Our team is carefully preparing these products in the best way possible. Like something baking to perfection, they'll be ready soon!",
                )}
              </p>
            </div>

            <div className="rounded-[1.6rem] border border-dashed border-white/80 bg-[rgba(255,255,255,0.52)] p-5 backdrop-blur text-center sm:text-left lg:text-left">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8d6476]">
                {t("comingSoon.collectionLabel", "Collection Status")}
              </p>
              <p className="mt-3 text-3xl font-black text-[#2d1f26]">0</p>
              <p className="mt-2 text-sm leading-6 text-[#6f5b64]">
                {isAllProductsPage
                  ? t(
                      "comingSoon.allProductsFallback",
                      "Catalog items will be listed here once they are published.",
                    )
                  : t(
                      "comingSoon.collectionFallback",
                      "No products are published in this collection yet.",
                    )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductGrid = ({
  products,
  onProductClick,
  currentLanguage,
  user,
  wishlist,
  title,
  subtitle,
  theme,
  isAllProductsPage,
  useLavenderBackdrop,
  twoColOnMobile = false,
  useFixedBackdrop = true,
  hideCollectionHeader = false,
  surfaceVariant = "default",
}) => {
  const { t } = useTranslation();
  const highlightedTags = useMemo(() => {
    return Array.from(
      new Set(
        products
          .flatMap((product) =>
            Array.isArray(product.tags) ? product.tags : [],
          )
          .filter(Boolean),
      ),
    ).slice(0, 4);
  }, [products]);

  return (
    <div
      className={`relative overflow-hidden ${
        surfaceVariant === "wallarts"
          ? "bg-transparent"
          : "bg-[rgb(247,239,239,0.35)]"
      }`}
    >
      {surfaceVariant !== "wallarts" && (
        <>
          <div
            className={`pointer-events-none absolute inset-0 [background-attachment:scroll] ${
              useFixedBackdrop ? "md:[background-attachment:fixed]" : ""
            } ${useLavenderBackdrop ? "opacity-90" : "opacity-80"}`}
            style={{
              backgroundImage: useLavenderBackdrop
                ? 'url("/images/lavander.webp")'
                : 'url("/images/canvas.webp")',
              backgroundPosition: "center",
              backgroundRepeat: useLavenderBackdrop ? "no-repeat" : "repeat",
              backgroundSize: useLavenderBackdrop ? "cover" : "1000px",
            }}
          />
          <div
            className={`absolute inset-0 ${
              useLavenderBackdrop
                ? "bg-black/5"
                : isAllProductsPage
                  ? "bg-[radial-gradient(circle_at_top_left,_rgba(170,127,150,0.16),_transparent_32%),radial-gradient(circle_at_85%_12%,_rgba(235,180,108,0.18),_transparent_24%),radial-gradient(circle_at_bottom_right,_rgba(131,55,91,0.12),_transparent_32%)]"
                  : "bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.2),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(131,55,91,0.1),_transparent_32%)]"
            }`}
          />
          <div
            className={`absolute inset-0 bg-[linear-gradient(to_right,rgba(131,55,91,0.12)_1px,transparent_1px),linear-gradient(to_bottom,rgba(131,55,91,0.12)_1px,transparent_1px)] bg-[size:34px_34px] ${
              isAllProductsPage ? "opacity-[0.12]" : "opacity-[0.06]"
            }`}
          />
        </>
      )}

      <section
        className={`relative px-4 pb-14 sm:px-6 lg:px-8  ${
          isAllProductsPage
            ? "pt-24 sm:pt-28 lg:pt-[9rem]"
            : useLavenderBackdrop
              ? "pt-12 lg:pt-12"
              : hideCollectionHeader
                ? "pt-4 lg:pt-4"
                : "pt-32 lg:pt-32"
        }`}
      >
        <div className="mx-0 sm:mx-3 lg:mx-10">
          {isAllProductsPage ? (
            <div
              className={`overflow-hidden rounded-[1.35rem] sm:rounded-[2rem] border border-white/70 bg-gradient-to-br ${theme.hero} shadow-[0_30px_80px_-40px_rgba(131,55,91,0.45)]`}
            >
              <div className="grid gap-6 px-4 py-6 sm:gap-8 sm:px-8 sm:py-8 lg:grid-cols-[1.4fr_0.9fr] lg:px-12 lg:py-12">
                <div className="text-center lg:text-left">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8d6476]">
                    {t("productsPage.hero.badge", "Curated Collection")}
                  </p>
                  <h1 className="mt-3 max-w-3xl text-3xl font-semibold tracking-tight text-[#2d1f26] sm:text-4xl lg:text-5xl">
                    {title || t("navbar.products", "Products")}
                  </h1>
                  <p className="mt-4 max-w-2xl text-sm leading-7 text-[#6f5b64] sm:text-base">
                    {subtitle ||
                      t(
                        "productsPage.hero.description",
                        "Explore our latest cross-border finds with thoughtful details, practical quality, and a cleaner shopping experience.",
                      )}
                  </p>

                  <div className="mt-6 flex flex-wrap gap-3 justify-center lg:justify-start">
                    <div className="rounded-full border border-white/80 bg-white/75 px-4 py-2 text-sm font-semibold text-[#4f3d45] shadow-sm backdrop-blur">
                      {products.length}{" "}
                      {products.length === 1
                        ? t("productsPage.hero.productSingular", "product")
                        : t("productsPage.hero.productPlural", "products")}
                    </div>
                    {highlightedTags.map((tag) => (
                      <span
                        key={tag}
                        className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] ${theme.chip}`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 self-end">
                  <div className="rounded-3xl border border-white/80 bg-white/70 p-4 backdrop-blur text-center sm:text-left">
                    <p className="text-xs uppercase tracking-[0.18em] text-[#967484]">
                      {t("productsPage.hero.styleLabel", "Style")}
                    </p>
                    <p className="mt-2 text-lg font-bold text-[#2d1f26]">
                      {t("productsPage.hero.styleTitle", "Handmade with care")}
                    </p>
                    <p className="mt-1 text-sm text-[#6f5b64]">
                      {t(
                        "productsPage.hero.styleDescription",
                        "Discover handmade pieces and thoughtfully selected items, each prepared with quality and attention to detail.",
                      )}
                    </p>
                  </div>
                  <div className="rounded-3xl border border-white/80 bg-white/70 p-4 backdrop-blur text-center sm:text-left">
                    <p className="text-xs uppercase tracking-[0.18em] text-[#967484]">
                      {t("productsPage.hero.selectionLabel", "Selection")}
                    </p>
                    <p className="mt-2 text-lg font-bold text-[#2d1f26]">
                      {t(
                        "productsPage.hero.selectionTitle",
                        "One collection, many favorites",
                      )}
                    </p>
                    <p className="mt-1 text-sm text-[#6f5b64]">
                      {t(
                        "productsPage.hero.selectionDescription",
                        "Browse products shared across our online platforms in one place, with easy access to the latest arrivals and best sellers.",
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : !hideCollectionHeader ? (
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
              <div className="text-center sm:text-left w-full sm:w-auto">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#8d6476]">
                  {title || t("navbar.products", "Products")}
                </p>
                <h2 className="mt-2 text-2xl font-black tracking-tight text-[#8A413B] sm:text-3xl">
                  {subtitle ||
                    t("productsPage.collectionTitle", "Shop the collection")}
                </h2>
              </div>
              {/* <div className="rounded-full border border-white/60 bg-white/55 px-4 py-2 text-sm font-semibold text-[#4f3d45] shadow-sm backdrop-blur">
                {products.length}{" "}
                {products.length === 1 ? "product" : "products"}
              </div> */}
            </div>
          ) : null}

          {products.length === 0 ? (
            <EmptyCollectionState
              title={title}
              subtitle={subtitle}
              theme={theme}
              isAllProductsPage={isAllProductsPage}
            />
          ) : (
            <div
              className={`${hideCollectionHeader ? "mt-2" : "mt-10"} ${
                isAllProductsPage
                  ? "rounded-[1.35rem] sm:rounded-[2rem] border border-white/70 bg-white/65 p-3 sm:p-6 shadow-[0_35px_90px_-55px_rgba(131,55,91,0.5)] backdrop-blur lg:p-8"
                  : ""
              }`}
            >
              {isAllProductsPage && (
                <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div className="text-center sm:text-left">
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#9a6b7f]">
                      {t(
                        "productsPage.shopSection.badge",
                        "Shop the Collection",
                      )}
                    </p>
                    <h2 className="mt-2 text-2xl font-bold text-[#2d1f26]">
                      {t(
                        "productsPage.shopSection.title",
                        "Explore what fits your style",
                      )}
                    </h2>
                  </div>
                  <p className="max-w-xl text-sm leading-6 text-[#6f5b64] text-center sm:text-left">
                    {t(
                      "productsPage.shopSection.description",
                      "Browse premium pieces with fast access to details, wishlist, and cart actions.",
                    )}
                  </p>
                </div>
              )}

              <div
                className={`grid ${
                  twoColOnMobile
                    ? "grid-cols-2 gap-3 sm:gap-8 sm:grid-cols-2"
                    : "grid-cols-1 gap-5 sm:gap-8 sm:grid-cols-2"
                } lg:grid-cols-3 xl:grid-cols-4`}
              >
                {products.map((product) => (
                  <ProductCard
                    key={product.product_id}
                    product={product}
                    onProductClick={onProductClick}
                    currentLanguage={currentLanguage}
                    user={user}
                    wishlist={wishlist}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const ProductDetailPage = ({ product, onBackClick, currentLanguage }) => {
  const { t } = useTranslation();

  const [mainImage, setMainImage] = useState(
    product.images && product.images[0] ? product.images[0].image_url : null,
  );

  if (!product) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <p className="text-gray-700 text-xl">
          {t("productsPage.productNotFound", "Product not found.")}
        </p>
      </div>
    );
  }

  const productName = product[`name_${currentLanguage}`] || product.name_en;
  const productDescription =
    product[`description_${currentLanguage}`] || product.description_en;
  const productPrice = `${t("productsPage.detail.currency", "£")}${product.price}`;
  const stockQuantity = product.stock_quantity;

  const handleThumbnailClick = (imagePath) => {
    setMainImage(imagePath);
  };

  return (
    <section className="py-12 bg-gray-100 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl bg-white rounded-xl shadow-lg p-6 md:p-10">
        <button
          onClick={onBackClick}
          className="mb-8 flex items-center text-gray-700 hover:text-gray-900 transition-colors duration-200"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            ></path>
          </svg>
          {t("backToProductsBtn", "Back to All Products")}
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="flex flex-col items-center">
            <div className="rounded-lg overflow-hidden shadow-md mb-4 w-full">
              <img
                src={
                  mainImage
                    ? getImageUrl(mainImage)
                    : `https://placehold.co/400x300/E0BBE4/FFFFFF?text=${encodeURIComponent(t("productsPage.detail.noImage", "No Image"))}`
                }
                alt={productName}
                className="w-full h-auto object-cover"
              />
            </div>
            {product.images && product.images.length > 1 && (
              <div className="flex space-x-2 overflow-x-auto w-full justify-center">
                {product.images.map((image, index) => (
                  <img
                    key={index}
                    src={getImageUrl(image.image_url)}
                    alt={t("productsPage.detail.thumbnailAlt", {
                      name: productName,
                      index: index + 1,
                      defaultValue: `${productName} thumbnail ${index + 1}`,
                    })}
                    className={`w-16 h-16 object-cover rounded-md cursor-pointer border-2 transition-colors
                                ${
                                  mainImage === image.image_url
                                    ? "border-[#AA7F96]"
                                    : "border-transparent"
                                }`}
                    onClick={() => handleThumbnailClick(image.image_url)}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <h1 className="text-3xl sm:text-4xl font-bold text-[#83375B] mb-4">
              {productName}
            </h1>
            <p className="text-2xl font-semibold text-gray-800 mb-6">
              {productPrice}
            </p>
            <h2 className="text-xl font-bold text-gray-800 mb-2">
              {t("productDescriptionTitle", "Description")}
            </h2>
            <div
              className="prose text-gray-700 mb-6 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(productDescription || ""),
              }}
            ></div>
            <p className="text-sm text-gray-500 mt-4">
              {t("inStock", "In Stock")}: {stockQuantity}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Product = ({
  pageKey = null,
  tag = null,
  title = "",
  subtitle = "",
  useFixedBackdrop = true,
  hideCollectionHeader = false,
  surfaceVariant = "default",
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [userWishlist, setUserWishlist] = useState([]); // ✅ New state for the wishlist

  // Effect to fetch products
  useEffect(() => {
    const getProducts = async () => {
      try {
        setLoading(true);
        const data = await fetchProductsFromDatabase(
          i18n.language,
          pageKey,
          tag,
        );
        setProducts(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    getProducts();
  }, [i18n.language, pageKey, tag]);

  // ✅ New effect to fetch the user's wishlist
  useEffect(() => {
    const getWishlist = async () => {
      if (user) {
        try {
          const response = await api.get("/user/wishlist");
          setUserWishlist(response.data);
        } catch (err) {
          console.error("Failed to fetch wishlist:", err);
          setUserWishlist([]);
        }
      } else {
        setUserWishlist([]); // Clear wishlist if user logs out
      }
    };
    getWishlist();
  }, [user]);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  const handleBackToProducts = () => {
    setSelectedProduct(null);
  };

  const searchQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get("q") || "").trim().toLowerCase();
  }, [location.search]);

  const filteredProducts = useMemo(() => {
    if (!searchQuery) {
      return products;
    }

    return products.filter((product) => {
      const names = [
        product.name_en,
        product.name_tr,
        product.name_ar,
        product.name_ja,
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());

      const descriptions = [
        product.description_en,
        product.description_tr,
        product.description_ar,
        product.description_ja,
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());

      const tags = Array.isArray(product.tags)
        ? product.tags.map((tagValue) => String(tagValue).toLowerCase())
        : [];

      return [...names, ...descriptions, ...tags].some((text) =>
        text.includes(searchQuery),
      );
    });
  }, [products, searchQuery]);

  const sortedProducts = useMemo(() => {
    const sectionKey = String(pageKey || tag || "")
      .trim()
      .toLowerCase();

    // Keep "All Products" search relevance order untouched.
    if (!sectionKey) return filteredProducts;

    return [...filteredProducts].sort((a, b) => {
      const aMeta = getPlacementSortMeta(a, sectionKey);
      const bMeta = getPlacementSortMeta(b, sectionKey);

      if (bMeta.featured !== aMeta.featured) {
        return bMeta.featured - aMeta.featured;
      }

      if (aMeta.order !== bMeta.order) {
        return aMeta.order - bMeta.order;
      }

      return Number(b.stock_quantity || 0) - Number(a.stock_quantity || 0);
    });
  }, [filteredProducts, pageKey, tag]);

  const isAllProductsPage = !pageKey && !tag;
  const useLavenderBackdrop = isWheatCollection(title, pageKey, tag);
  const twoColOnMobile =
    String(pageKey || "")
      .trim()
      .toLowerCase() === "wheatbag";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fffafc] px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl animate-pulse">
          <div className="rounded-[2rem] border border-white/70 bg-white/70 p-8 shadow-[0_30px_80px_-50px_rgba(131,55,91,0.45)]">
            <div className="h-4 w-32 rounded-full bg-[#f1dfe7]" />
            <div className="mt-4 h-12 w-80 max-w-full rounded-2xl bg-[#ecd7e1]" />
            <div className="mt-4 h-4 w-[32rem] max-w-full rounded-full bg-[#f4e7ed]" />
          </div>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-[1.5rem] border border-white/70 bg-white/70 shadow-sm"
              >
                <div className="h-64 bg-[#efe3e9]" />
                <div className="space-y-3 p-4">
                  <div className="h-6 rounded-full bg-[#f2e5eb]" />
                  <div className="h-4 w-2/3 rounded-full bg-[#f6edf1]" />
                  <div className="h-10 rounded-xl bg-[#efe2e8]" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#fffafc] px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl rounded-[2rem] border border-[#f1cbd7] bg-white px-8 py-14 text-center shadow-[0_35px_80px_-55px_rgba(131,55,91,0.45)]">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#fdecef] text-2xl text-[#b14969]">
            !
          </div>
          <h2 className="mt-5 text-2xl font-bold text-[#3d2630]">
            {t(
              "errorFetchingProducts",
              "Error fetching products. Please try again later.",
            )}
          </h2>
          <p className="mt-3 text-sm leading-6 text-[#7b5f6b]">{error}</p>
        </div>
      </div>
    );
  }

  const theme = getThemeByCollection(title, pageKey, tag);

  return (
    <div>
      {selectedProduct ? (
        <ProductDetailPage
          product={selectedProduct}
          onBackClick={handleBackToProducts}
          currentLanguage={i18n.language}
        />
      ) : (
        <ProductGrid
          products={sortedProducts}
          onProductClick={handleProductClick}
          currentLanguage={i18n.language}
          user={user}
          wishlist={userWishlist}
          title={title}
          subtitle={subtitle}
          theme={theme}
          isAllProductsPage={isAllProductsPage}
          useLavenderBackdrop={useLavenderBackdrop}
          twoColOnMobile={twoColOnMobile}
          useFixedBackdrop={useFixedBackdrop}
          hideCollectionHeader={hideCollectionHeader}
          surfaceVariant={surfaceVariant}
        />
      )}
    </div>
  );
};

export default Product;
